import {useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {callFunction, ApiFailure} from '@/lib/api/client';
import {fetchAvailability} from '@/lib/api/public';
import {parseRequest} from '@/lib/assistant/nlparse';
import {filterLocalSlots, pickService, toSlotQuery, wantsSlots, type LocalSlot} from '@/lib/assistant/local';
import {formatMoney} from '@/lib/money';
import {formatZoned, zonedDateKey, zonedParts} from '@/lib/time/tz';
import type {ApiAssistantReply, ApiService, ApiTenant} from '@/types/api';

export interface ChatSlot {
  iso: string;
  label: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Кнопки со свободными окнами: ведут сразу к вводу контактов. */
  slots?: ChatSlot[];
  serviceId?: string;
  /** Быстрые ответы (названия услуг). */
  choices?: string[];
}

const DAY_MS = 86_400_000;

async function answerLocally(
  message: string,
  tenant: ApiTenant,
  services: ApiService[],
): Promise<ChatMessage | null> {
  if (!wantsSlots(message)) return null;
  const parsed = parseRequest(message);
  const service = pickService(services, parsed.serviceKeyword);
  if (!service) {
    if (parsed.serviceKeyword === null && services.length > 0) {
      return {
        role: 'assistant',
        content: 'С удовольствием подберу время. Какая услуга нужна? Выберите или напишите своими словами.',
        choices: services.slice(0, 6).map((s) => s.name),
      };
    }
    return null;
  }

  const now = new Date();
  const todayKey = zonedDateKey(now, tenant.timezone);
  const tomorrowKey = zonedDateKey(new Date(now.getTime() + DAY_MS), tenant.timezone);
  const availability = await fetchAvailability(tenant.slug, service.id, todayKey, 14);
  const all: LocalSlot[] = availability.days.flatMap((day) =>
    day.slots.map((iso) => ({
      iso,
      dateKey: day.date,
      hour: zonedParts(new Date(iso), tenant.timezone).hour,
    })),
  );

  const query = toSlotQuery(parsed, todayKey, tomorrowKey);
  const matched = filterLocalSlots(all, query);
  const pool = matched.length > 0 ? matched : all;
  const pick = pool.slice(0, 6);

  const label = (slot: LocalSlot) => {
    const date = new Date(slot.iso);
    const dayWord = slot.dateKey === todayKey ? 'сегодня' : slot.dateKey === tomorrowKey ? 'завтра' : formatZoned(date, 'd MMMM', tenant.timezone);
    return `${dayWord}, ${formatZoned(date, 'HH:mm', tenant.timezone)}`;
  };

  const price = formatMoney(service.priceMinor, service.currency, tenant.locale);
  const head = `Подобрал услугу «${service.name}» — ${price}, около ${service.durationMinutes} мин.`;
  let tail: string;
  if (pick.length === 0) {
    tail = 'В ближайшие две недели свободных окон нет. Позвоните в студию — подскажем по времени.';
  } else if (matched.length > 0) {
    tail = 'Нашёл свободное время по вашему запросу. Выберите окно — оформим запись за пару касаний.';
  } else {
    tail = 'На желаемое время окон нет, вот ближайшие свободные. Выберите подходящее.';
  }

  return {
    role: 'assistant',
    content: `${head} ${tail}`,
    serviceId: service.id,
    slots: pick.map((slot) => ({iso: slot.iso, label: label(slot)})),
  };
}

/**
 * Чат помощника: сначала бесплатный разбор на сайте («мойку завтра после 6»),
 * для остальных вопросов — серверный помощник студии.
 */
export function useAssistantChat(slug: string, scope: 'client' | 'owner', context?: {tenant: ApiTenant; services: ApiService[]}, accessToken?: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const mutation = useMutation({
    mutationFn: async (message: string): Promise<ChatMessage> => {
      if (scope === 'client' && context) {
        try {
          const local = await answerLocally(message, context.tenant, context.services);
          if (local) return local;
        } catch {
          /* если подбор не удался — отвечает серверный помощник */
        }
      }
      const history = messages.map((m) => ({role: m.role, content: m.content}));
      const reply = await callFunction<ApiAssistantReply>('assistant', {
        body: {slug, scope, message, history},
        accessToken,
      });
      return {role: 'assistant', content: reply.reply};
    },
  });

  async function send(text: string) {
    const message = text.trim();
    if (!message) return;
    setMessages((prev) => [...prev, {role: 'user', content: message}]);
    try {
      const reply = await mutation.mutateAsync(message);
      setMessages((prev) => [...prev, reply]);
    } catch (error) {
      const content = error instanceof ApiFailure ? error.message : 'Помощник недоступен. Запись доступна обычным способом.';
      setMessages((prev) => [...prev, {role: 'assistant', content}]);
    }
  }

  return {messages, send, isPending: mutation.isPending};
}
